import { useStore } from '@nanostores/react';
import { isCartOpen, cartData } from '@stores/cartStore';
import { checkoutData } from '@stores/checkoutStore';
import va from '@vercel/analytics';
import {
	updateCart,
	updateCartDiscountCodes,
	updateCartNote,
	updateCartAttributes,
	updateCheckoutAttributes,
	getCart,
} from '@api/shopify';

import { chord } from '@api/chord.js';

import { addUrlParameter, deepEqual, getCookie } from '@utils/utils';


/**
 * Updates Nosto cart state with current cart lines
 * @param {Array} cartLines - Array of cart line edges from Shopify
 */
export const updateNostoCart = (cartLines) => {
	if (!cartLines || !Array.isArray(cartLines)) {
		console.warn('updateNostoCart: Invalid cartLines provided');
		return;
	}

	try {
		const nostoCartLines = cartLines.map((line) => {
			const productId = line.node.merchandise.product.id;
			const productIdNumber = productId.split('/').pop();
			const skuId = line.node.merchandise.id;
			const skuIdNumber = skuId.split('/').pop();
			return {
				name: line.node.merchandise.product.title,
				price_currency_code: 'USD',
				product_id: productIdNumber,
				quantity: line.node.quantity,
				sku_id: skuIdNumber,
				unit_price: Number(line.node.cost.totalAmount.amount),
			};
		});

		if (typeof nostojs !== 'undefined') {
			nostojs((api) => {
				api.defaultSession()
					.setCart({
						items: nostoCartLines,
					})
					.viewCart()
					.update()
					.then((response) => {
						console.log('✅ Nosto cart updated successfully');
						return response;
					})
					.catch((error) => {
						console.error('❌ Nosto cart update error:', error);
					});
			});
		}
	} catch (error) {
		console.error('❌ Error in updateNostoCart:', error);
	}
};

export const handleQuantityChange = (cartId, id, quantity, type, setIsUpdatingCart = null, setItemQuantityCapped = null) => {
	if (quantity === -1 || quantity === '-1') {
		return;
	}

	if (type === 'increment') {
		quantity = parseInt(quantity) + 1;
	} else if (type === 'decrement') {
		quantity = parseInt(quantity) - 1;
	}

	if (setIsUpdatingCart) setIsUpdatingCart({itemId: id, operation: 'quantity'});

	let lines = {
		id: id,
		quantity: quantity,
	};

	updateCart(cartId, lines).then((updateCartData) => {
		let cartLines = updateCartData.body.cartLinesUpdate.cart.lines.edges;
		let cartCost = updateCartData.body.cartLinesUpdate.cart.cost;
		let discountCodes =
			updateCartData.body.cartLinesUpdate.cart.discountCodes;
		let discountAllocations = updateCartData.body.cartLinesUpdate.cart.discountAllocations;

		// check if shopify capped the quantity due to inventory limits
		const updatedLine = cartLines.find(line => line.node.id === id);
		if (updatedLine && updatedLine.node.quantity < quantity) {
			if (setItemQuantityCapped) {
				setItemQuantityCapped({itemId: id});

				setTimeout(() => {
					setItemQuantityCapped(null);
				}, 3000);
			}
		}

		let hasGiftCardInCart = cartLines?.some(
			(line) =>
				line.node?.merchandise?.id ===
				'gid://shopify/ProductVariant/41447180697662'
		);

		let cartObj = {
			...cartData.get(),
			lines: cartLines,
			cost: cartCost,
			discountCodes: discountCodes,

			discountAllocations: discountAllocations,

			freeGiftCardAdded: hasGiftCardInCart,
		};
		if (cartLines.length === 0) {
			let discounts = document.getElementsByClassName('remove-discount');
			if (discounts.length > 0) {
				for (let i = 0; i < discounts.length; i++) {
					discounts[i].click();
				}
			}

			cartObj = {
				...cartData.get(),
				lines: [],
				cost: {},
				discountCodes: [],
			};
		}

		cartData.set(cartObj);


		if (setIsUpdatingCart) setIsUpdatingCart(null);

		// Update Nosto cart state
		updateNostoCart(cartLines);

	}).finally(() => {
		if (setIsUpdatingCart) setIsUpdatingCart(null);
	});
};

export const handleRemoveDiscount = (
	cartId,
	currentDiscountCodes,
	discountCode
) => {
	console.log('handleRemoveDiscount called - removing discount code:', { cartId, discountCode, currentDiscountCodes });
	if (discountCode) {
		// Filter out the discount to remove and map to code strings
		let newDiscountCodes = currentDiscountCodes
			.filter((discount) => discount.code !== discountCode)
			.map((discount) => discount.code);

		console.log('Updating cart with new discount codes:', newDiscountCodes);

		updateCartDiscountCodes(cartId, newDiscountCodes).then((data) => {
			console.log('updateCartDiscountCodes response:', data);

			let cartCost = data?.body?.cartDiscountCodesUpdate?.cart?.cost;
			let cartLines = data?.body?.cartDiscountCodesUpdate?.cart?.lines?.edges;
			let discountCodes =
				data?.body?.cartDiscountCodesUpdate?.cart?.discountCodes;
			let discountAllocations = data?.body?.cartDiscountCodesUpdate?.cart?.discountAllocations;

			// Ensure we have valid cart data before updating
			if (cartCost && cartLines) {
				let cartObj = {
					...cartData.get(),
					cost: cartCost,
					lines: cartLines,
					discountCodes: discountCodes,
					discountAllocations: discountAllocations,
				};

				chord.trackCouponRemoved({
					cartId: cartId.replace('gid://shopify/Cart/', ''),
					couponName: discountCode,
				});

				cartData.set(cartObj);

				// Update Nosto cart state
				updateNostoCart(cartLines);
			} else {
				// If the discount update response doesn't include full cart data,
				// fetch the fresh cart to ensure we have complete data
				console.log('Discount update response missing cart data, fetching fresh cart...');
				getCart(cartId).then((freshCartData) => {
					console.log('Fresh cart data:', freshCartData);
					if (freshCartData?.body?.cart) {
						const freshCart = freshCartData.body.cart;
						let cartObj = {
							...cartData.get(),
							cost: freshCart.cost,
							lines: freshCart.lines.edges,
							discountCodes: freshCart.discountCodes,
							discountAllocations: freshCart.discountAllocations,
						};

						chord.trackCouponRemoved({
							cartId: cartId.replace('gid://shopify/Cart/', ''),
							couponName: discountCode,
						});

						cartData.set(cartObj);

						// Update Nosto cart state
						updateNostoCart(freshCart.lines.edges);
					} else {
						console.error('Fresh cart data is missing cart object');
					}
				}).catch((error) => {
					console.error('Failed to fetch fresh cart after discount removal:', error);
				});
			}
		}).catch((error) => {
			console.error('Failed to update cart discount codes:', error);
			// Fallback: fetch fresh cart data
			getCart(cartId).then((freshCartData) => {
				if (freshCartData?.body?.cart) {
					const freshCart = freshCartData.body.cart;
					let cartObj = {
						...cartData.get(),
						cost: freshCart.cost,
						lines: freshCart.lines.edges,
						discountCodes: freshCart.discountCodes,
						discountAllocations: freshCart.discountAllocations,
					};

					chord.trackCouponRemoved({
						cartId: cartId.replace('gid://shopify/Cart/', ''),
						couponName: discountCode,
					});

					cartData.set(cartObj);
				}
			}).catch((fallbackError) => {
				console.error('Failed to fetch fresh cart after error:', fallbackError);
			});
		});
	}
};

// New function to remove all Inveterate discount codes at once
export const handleRemoveAllInveterateDiscounts = (cartId, currentDiscountCodes) => {
	console.log('handleRemoveAllInveterateDiscounts called - removing all Inveterate discount codes:', { cartId, currentDiscountCodes });

	// Filter out all Inveterate discount codes (those starting with 'REDEEM+')
	let newDiscountCodes = currentDiscountCodes.filter((discount) => {
		return !discount.code.startsWith('REDEEM+');
	});

	console.log('Filtered discount codes (removing all Inveterate):', {
		original: currentDiscountCodes,
		filtered: newDiscountCodes
	});

	// Return a Promise that resolves after the cart is updated
	return updateCartDiscountCodes(cartId, newDiscountCodes).then((data) => {
		// Always fetch fresh cart data to ensure we have the complete updated state
		console.log('Discount codes updated, fetching fresh cart data...');
		return getCart(cartId).then((freshCartData) => {
			if (freshCartData?.body?.cart) {
				const freshCart = freshCartData.body.cart;
				let cartObj = {
					...cartData.get(),
					cost: freshCart.cost,
					lines: freshCart.lines.edges,
					discountCodes: freshCart.discountCodes,
					discountAllocations: freshCart.discountAllocations,
				};

				// Track removal of all Inveterate discounts
				currentDiscountCodes.forEach(discount => {
					if (discount.code.startsWith('REDEEM+')) {
						chord.trackCouponRemoved({
							cartId: cartId.replace('gid://shopify/Cart/', ''),
							couponName: discount.code,
						});
					}
				});

				console.log('Updated cart data after removing Inveterate discounts:', cartObj);
				cartData.set(cartObj);

				// Update Nosto cart state
				updateNostoCart(freshCart.lines.edges);
				return true;
			} else {
				console.error('Failed to get fresh cart data after discount removal');
				throw new Error('Failed to get fresh cart data after discount removal');
			}
		});

	});
};

// Nosto session token sync function
export async function checkTokenToCustomer(cartIdGid, shopIdGid, providedCustomerId) {
	let customerId = providedCustomerId || getCookie('2c.cId');
	// Extract cart token and remove query parameters if present
	const cartToken = cartIdGid?.replace('gid://shopify/Cart/', '').split('?')[0];
	const shopId = shopIdGid?.replace('gid://shopify/Shop/', 'shopify-');

	console.log('🟡 NOSTO DEBUG: Starting cart session token sync');
	console.log('🟡 NOSTO DEBUG: Parameters:', {
		cartIdGid,
		shopIdGid,
		providedCustomerId,
		customerId,
		cartToken,
		shopId
	});

	if (!customerId || !cartToken || !shopId) {
		console.warn('🔴 NOSTO DEBUG: Missing required parameters for token sync', {
			customerId: !!customerId,
			cartToken: !!cartToken,
			shopId: !!shopId
		});
		return;
	}

	const NOSTO_URL = `https://connect.nosto.com/token/${shopId}/${customerId}/${cartToken}`;
	console.log('🟡 NOSTO DEBUG: Request URL:', NOSTO_URL);

	try {
		console.log('🟡 NOSTO DEBUG: Making fetch request...');
		const response = await fetch(NOSTO_URL);
		console.log('🟢 NOSTO DEBUG: Response received:', {
			status: response.status,
			statusText: response.statusText,
			ok: response.ok,
			headers: Object.fromEntries(response.headers.entries())
		});

		// Clone response to read body while preserving original
		const responseClone = response.clone();
		try {
			const responseText = await responseClone.text();
			console.log('🟢 NOSTO DEBUG: Raw response body:', responseText);

			// Try to parse as JSON if possible
			if (responseText) {
				try {
					const responseJson = JSON.parse(responseText);
					console.log('🟢 NOSTO DEBUG: Parsed JSON response:', responseJson);
				} catch (parseError) {
					console.log('🟡 NOSTO DEBUG: Response is not JSON, raw text logged above');
				}
			} else {
				console.log('🟡 NOSTO DEBUG: Empty response body');
			}
		} catch (bodyError) {
			console.error('🔴 NOSTO DEBUG: Could not read response body:', bodyError);
		}

		if (!response.ok) {
			console.error('🔴 NOSTO DEBUG: Token sync failed with status:', response.status);
		} else {
			console.log('🟢 NOSTO DEBUG: Token sync successful!');
		}
		return response;
	} catch (err) {
		console.error('🔴 NOSTO DEBUG: Error updating cart:', err);
		throw err;
	}
}

export const handleCheckout = async (checkoutUrl) => {
	console.log('DEBUG: handleCheckout called with URL:', checkoutUrl);
	if (checkoutUrl) {
		let $cartData = cartData.get();
		console.log('DEBUG: Cart data:', $cartData);

		// chord.trackCheckoutStarted({ checkout: $cartData })

		const cartLinesArray = $cartData?.lines.map((line) => {
			let item = line.node;

			const str = line.node.merchandise.id;
			const n = str.lastIndexOf('/');
			const lineItemId = str.substring(n + 1);

			let product = {
				item_name: item.merchandise.product.title,
				item_id: lineItemId,
				price: Number(item.cost.totalAmount.amount),
				item_category:
					item.merchandise.product?.options[0]?.values[0] || '',
				item_category2:
					item.merchandise.product?.options[1]?.values[0] || '',
				item_category3:
					item.merchandise.product?.options[2]?.values[0] || '',
				item_variant: lineItemId,
				quantity: item.quantity,
			};

			return product;
		});

		// Analytics tracking with error handling
		try {
			console.log('DEBUG: Pushing to dataLayer');
			dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
			dataLayer.push({
				event: 'dl_begin_checkout',
				ecommerce: {
					items: cartLinesArray,
					value: Number($cartData?.cost?.totalAmount?.amount),
				},
			});

			dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
			dataLayer.push({
				event: 'GA4_begin_checkout',
				currency: 'USD',
				ecommerce: {
					items: cartLinesArray,
					value: Number($cartData?.cost?.totalAmount?.amount),
				},
			});

			console.log('DEBUG: Tracking with Vercel Analytics');
			va.track('begin_checkout', {
				cart_id: $cartData?.id,
				cart_items: cartLinesArray,
				cart_total: $cartData?.cost?.totalAmount?.amount,
			});
		} catch (error) {
			console.error('DEBUG: Error in analytics tracking:', error);
		}

		// Nosto viewCart with error handling
		try {
			console.log('DEBUG: Calling nostojs viewCart');
			nostojs((api) => {
				api.defaultSession()
					.viewCart()
					.setPlacements(['cart-related'])
					.load()
					.then((data) => {
						console.log('DEBUG: Nosto viewCart completed');
					})
					.catch((error) => {
						console.error('DEBUG: Nosto viewCart error:', error);
					});
			});
		} catch (error) {
			console.error('DEBUG: Error calling nostojs:', error);
		}

				// Nosto session token sync before checkout with timeout
		console.log('🟡 NOSTO DEBUG: Initiating session token sync before checkout');
		try {
			const cartId = $cartData?.cartId;
			console.log('🟡 NOSTO DEBUG: Cart ID for sync:', cartId);
			if (cartId) {
				// Add timeout to prevent hanging
				const timeoutPromise = new Promise((_, reject) =>
					setTimeout(() => reject(new Error('Token sync timeout')), 3000)
				);

				await Promise.race([
					checkTokenToCustomer(cartId, 'gid://shopify/Shop/23382917', null),
					timeoutPromise
				]);
				console.log('🟢 NOSTO DEBUG: Session token sync completed successfully');
			} else {
				console.warn('🔴 NOSTO DEBUG: No cart ID available for session token sync');
			}
		} catch (error) {
			console.error('🔴 NOSTO DEBUG: Nosto session token sync failed:', error);
			// Continue with checkout even if token sync fails
		}

		const goToCheckout = () => {
			console.log('DEBUG: About to redirect to checkout URL:', checkoutUrl);
			window.location = checkoutUrl;
		};

		// Return a promise that resolves after redirect is initiated
		console.log('DEBUG: Setting 500ms timeout before redirect');
		return new Promise((resolve) => {
			setTimeout(() => {
				console.log('DEBUG: Timeout completed, calling goToCheckout');
				goToCheckout();
				console.log('DEBUG: Redirect initiated, resolving promise');
				resolve();
			}, 500);
		});
	} else {
		console.error('DEBUG: No checkoutUrl provided to handleCheckout');
		return Promise.reject(new Error('No checkout URL provided'));
	}
};

export const manageDiscounts = (cartId, discountCode) => {
	chord.trackCouponEntered({
		cartId: cartId.replace('gid://shopify/Cart/', ''),
		couponName: discountCode,
	});

	return new Promise((resolve) => {
		updateCartDiscountCodes(cartId, discountCode)
			.then((data) => {
				let discountCodes =
					data.body.cartDiscountCodesUpdate.cart.discountCodes;

				if (!discountCodes[0]?.applicable) {
					chord.trackCouponDenied({
						cartId: cartId.replace('gid://shopify/Cart/', ''),
						couponName: discountCode,
					});

					resolve(
						`We're sorry, ${discountCode} is not a valid discount code`
					);
				} else {
					let cartCost = data.body.cartDiscountCodesUpdate.cart.cost;
					let cartLines =
						data.body.cartDiscountCodesUpdate.cart.lines.edges;
					let discountAllocations = data.body.cartDiscountCodesUpdate.cart.discountAllocations;

					let cartObj = {
						...cartData.get(),
						cost: cartCost,
						lines: cartLines,
						discountCodes: discountCodes,
						discountAllocations: discountAllocations,
					};
					cartData.set(cartObj);

					// Update Nosto cart state
					updateNostoCart(cartLines);

					chord.trackCouponApplied({
						cartId: cartId.replace('gid://shopify/Cart/', ''),
						couponName: discountCode,
					});

					resolve('');
				}
			})
			.catch((error) => {
				// Handle any errors that occurred during the asynchronous operations
				resolve('An error occurred while applying the discount');
			});
	});
};

export const handleRemoveBundle = (cartId, id, bundle_id, bundle_size, setIsUpdatingCart = null) => {
	if (setIsUpdatingCart) setIsUpdatingCart({itemId: id, operation: 'removeBundle'});

	let $cartData = cartData.get();
	let lines;
	let cartLines = $cartData.lines;

	let filteredLines = cartLines.map((line) => {
		let bundle_info =
			line.node.attributes[0]?.key === '_bundle_info'
				? JSON.parse(line.node.attributes[0].value)
				: null;
		if (
			bundle_info &&
			bundle_info.bundle_id.toString() === bundle_id.toString() &&
			bundle_info.bundle_size === bundle_size
		) {
			return {
				quantity: 0,
				id: line.node.id,
			};
		}
	});
	filteredLines = filteredLines.filter((line) => {
		if (line !== undefined) {
			return line;
		}
	});

	updateCart(cartId, filteredLines).then((updateCartData) => {
		let cartLines = updateCartData.body.cartLinesUpdate.cart.lines.edges;
		let cartCost = updateCartData.body.cartLinesUpdate.cart.cost;
		let discountCodes =
			updateCartData.body.cartLinesUpdate.cart.discountCodes;
		let discountAllocations = updateCartData.body.cartLinesUpdate.cart.discountAllocations;

		let cartObj = {
			...cartData.get(),
			lines: cartLines,
			cost: cartCost,
			discountCodes: discountCodes,
			discountAllocations: discountAllocations,
		};

		cartData.set(cartObj);

		if (setIsUpdatingCart) setIsUpdatingCart(null);
		// Update Nosto cart state
		updateNostoCart(cartLines);
	});
};

export const handleRemoveItem = (
	cartId,
	id,
	quantity,
	hasEmbroidery,
	variantId,
	setIsUpdatingCart = null
) => {
	if (setIsUpdatingCart) setIsUpdatingCart({itemId: id, operation: 'remove'});

	let $cartData = cartData.get();
	let lines;
	let cartLines = $cartData.lines;

	//

	let itemRemovedId = variantId?.split('/').pop();

	let freeGiftCardRemoved = false;

	if (itemRemovedId === 'gid://shopify/ProductVariant/41447180697662') {
		freeGiftCardRemoved = true;
	}

	if (hasEmbroidery) {
		let matchingEmbroideryOption = cartLines.find((line) => {
			return (
				line.node.merchandise.product.title === 'Embroidery Option' &&
				line.node.attributes[0].value.toString() ===
					itemRemovedId.toString()
			);
		});

		let embroideryOptionId = matchingEmbroideryOption?.node?.id;
		let matchingEmbroideryOptionId =
			matchingEmbroideryOption?.node?.attributes[0]?.value;
		let embroideryOptionQuantity = matchingEmbroideryOption?.node?.quantity;
		if (
			matchingEmbroideryOptionId &&
			matchingEmbroideryOptionId.toString() === itemRemovedId.toString()
		) {
			lines = [
				{
					id: embroideryOptionId,
					quantity: embroideryOptionQuantity - quantity,
				},
				{
					id: id,
					quantity: 0,
				},
			];
		} else {
			lines = {
				id: id,
				quantity: 0,
			};
		}
	} else {
		lines = {
			id: id,
			quantity: 0,
		};
	}

	if (cartLines && cartLines.length > 0) {
		let items = cartLines;
		let itemRemoved = items.filter((item) => {
			return item.node.id === id;
		});

		let gtmItems = itemRemoved.map((item) => {
			let itemId = item.node.merchandise.id;
			itemId = itemId.split('/').pop();
			let product = {
				item_name: item?.node?.merchandise?.product?.title,
				item_id: itemId ? String(itemId) : '',
				price: Number(item?.node?.cost?.totalAmount?.amount) || '',
				item_category:
					item.node.merchandise.product?.options[0]?.values[0] || '',
				item_category2:
					item.node.merchandise.product?.options[1]?.values[0] || '',
				item_category3:
					item.node.merchandise.product?.options[2]?.values[0] || '',
				item_variant: String(item?.node?.merchandise?.title),
				quantity: item?.node?.quantity || 1,
			};

			return product;
		});

		chord.trackProductRemoved({
			cart: $cartData,
			lineItem: itemRemoved?.[0]?.node,
		});

		dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
		dataLayer.push({
			event: 'dl_remove_from_cart',
			ecommerce: {
				items: gtmItems,
			},
		});

		dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
		dataLayer.push({
			event: 'GA4_remove_from_cart',
			ecommerce: {
				items: gtmItems,
			},
		});

		// Update Nosto cart state
		updateNostoCart(cartLines);
	}

	updateCart(cartId, lines).then((updateCartData) => {
		let cartLines = updateCartData.body.cartLinesUpdate.cart.lines.edges;
		let cartCost = updateCartData.body.cartLinesUpdate.cart.cost;
		let discountCodes =
			updateCartData.body.cartLinesUpdate.cart.discountCodes;
		let discountAllocations = updateCartData.body.cartLinesUpdate.cart.discountAllocations;

		const freeGiftCardLine = cartLines.find((line) => {
			return line.node?.merchandise?.product?.sku === 'E-GIFT-CARD-20';
		});

		let cartObj = {
			...cartData.get(),
			lines: cartLines,
			cost: cartCost,
			discountCodes: discountCodes,
			discountAllocations: discountAllocations,
			freeGiftCardAdded: freeGiftCardLine ? true : false,
		};

		cartData.set(cartObj);

		// update the cart object if there are no more items in the cart
		if (cartLines.length === 0) {
			let discounts = document.getElementsByClassName('remove-discount');
			if (discounts.length > 0) {
				for (let i = 0; i < discounts.length; i++) {
					discounts[i].click();
				}
			}

			let cartObj = {
				...cartData.get(),
				lines: [],
				cost: {},
				discountCodes: [],
				freeGiftCardAdded: false,
			};

			cartData.set(cartObj);
		}

		if (cartLines.length === 1 && cartData.get().freeGiftCardAdded) {
			let cartObj = {
				...cartData.get(),
				lines: [],
				cost: {},
				discountCodes: [],
				freeGiftCardAdded: false,
			};
			cartData.set(cartObj);
		}

		const nostoCartLines = cartLines.map((line) => {
			const productId = line.node.merchandise.product.id;
			const productIdNumber = productId.split('/').pop();
			const skuId = line.node.merchandise.id;
			const skuIdNumber = skuId.split('/').pop();
			return {
				name: line.node.merchandise.product.title,
				price_currency_code: 'USD',
				product_id: productIdNumber,
				quantity: line.node.quantity,
				sku_id: skuIdNumber,
				unit_price: Number(line.node.cost.totalAmount.amount),
			};
		});

		nostojs((api) => {
			api.defaultSession()
				.setCart({
					items: nostoCartLines,
				})
				.viewCart()
				.update()
				.then((response) => {
					// Handle the response here
					return response;
				})
				.catch((error) => {
					console.error('Nosto cart update error:', error);
				});
		});

		if (setIsUpdatingCart) setIsUpdatingCart(null);
		// Update Nosto cart state
		updateNostoCart(cartLines);
	});
};

export const handleNoteSubmit = (cartId, giftNote) => {
	let encodedGiftNote = encodeURIComponent(giftNote);
	updateCartNote(cartId, giftNote).then((data) => {
		let cartNote = data.body.cartNoteUpdate.cart.note;
		let checkoutUrl = cartData.get().checkoutUrl;
		let newUrl = '';

		if (checkoutUrl) {
			newUrl = addUrlParameter(
				checkoutUrl,
				'giftNote',
				encodedGiftNote,
				true
			);

			newUrl = addUrlParameter(newUrl, 'giftWrap', 'YES', true);
		}
		let cartObj = {
			...cartData.get(),
			giftNote: cartNote,
			giftWrap: true,
			checkoutUrl: newUrl,
		};

		cartData.set(cartObj);
	});

	const attributes = [
		{
			key: 'giftWrap',
			value: 'YES',
		},
	];
};

export const handleCheckoutAttributes = (cartId, giftWrap, giftNote) => {
	let attributes = [
		{
			key: 'gift_wrap',
			value: 'YES',
		},
		{
			key: 'gift_note',
			value: giftNote,
		},
	];

	const input = {
		customAttributes: attributes,
		note: 'gift_note: ' + giftNote,
	};

	updateCheckoutAttributes(cartId, input).then((data) => {
		let cartObj = {
			...cartData.get(),
			checkoutAttributes:
				data?.body?.checkoutAttributesUpdate?.checkout
					?.customAttributes || [],
		};

		cartData.set(cartObj);
	});
};

function checkCorrectValue(value) {
	// Check if the anonymousId is a non-empty string
	if (typeof value === 'string' && value.trim() !== '') {
		return true;
	}

	return false;
}

/**
 * Used by Chord. Persist tracking parameters as cart attributes.
 */
export const updateCartTrackingAttributes = async (cartId, giftAttr = null) => {
	const cje = getCookie('cje') || 'cje_cookie';
	try {
		//
		let input;
		if (giftAttr) {
			// ?.anonymousId?.()) &&
			// 		checkCorrectValue(getCookie("_ga")?.substring(6)),
			// 	checkCorrectValue(window.analytics?.user?.()?.anonymousId?.()),
			// 	checkCorrectValue(getCookie("_ga")?.substring(6))
			// );
			if (
				checkCorrectValue(
					window.analytics?.user?.()?.anonymousId?.()
				) &&
				checkCorrectValue(getCookie('_ga')?.substring(6))
			) {
				//
				input = [
					{
						key: 'segmentId',
						value: window.analytics?.user?.()?.anonymousId?.(),
					},
					{
						key: 'googleClientId',
						value: getCookie('_ga')?.substring(6),
					},
					{
						key: 'cjevent',
						value: cje ? cje : '',
					},
					...giftAttr,
				];
			} else {
				//
				input = [
					...giftAttr,
					{ key: 'cjevent', value: cje ? cje : '' },
				];
			}
		} else {
			input = [
				{
					key: 'segmentId',
					value: window.analytics?.user?.()?.anonymousId?.(),
				},
				{
					key: 'googleClientId',
					value: getCookie('_ga')?.substring(6),
				},
				{
					key: 'cjevent',
					value: cje ? cje : '',
				},
			];
		}

		return updateCartAttributes(cartId, input);
	} catch (error) {
		console.error('Error updating cart tracking attributes', error);
		return;
	}
};
